/**
 * useBarrelEnergy — Live energy/cost calculation for a steam barrel cycle
 *
 * Computes running energy (kWh) and cost (£) from output_pct history + Agile rates.
 * Energy = sum of (avgDuty / 100) × BARREL_ELEMENT_KW × slotDurationHours per 30-min slot.
 *
 * Cycle timing derived from:
 * - cycleStartOverride: detected warm-up start from temperature history
 * - remaining_time: minutes remaining → cycleEnd = now + remaining
 */

import { useState, useEffect, useMemo } from 'react'
import { supabase } from '@/supabase-client'

const BARREL_ELEMENT_KW = 4.5
const AGILE_REGION = 'N'
const SLOT_DURATION_MS = 30 * 60 * 1000 // 30 minutes

export function useBarrelEnergy({ status, outputHistory, cycleStartOverride }) {
  const [rates, setRates] = useState([])
  const [loading, setLoading] = useState(false)

  // Derive cycle timing: start from detected warm-up, end from remaining_time
  const { cycleStart, cycleEnd } = useMemo(() => {
    const remainingMinutes = status?.remaining_time?.value != null
      ? Number(status.remaining_time.value)
      : null

    if (remainingMinutes == null || !cycleStartOverride) {
      return { cycleStart: null, cycleEnd: null }
    }

    const end = new Date(Date.now() + remainingMinutes * 60 * 1000)
    return { cycleStart: cycleStartOverride, cycleEnd: end }
  }, [status?.remaining_time?.value, cycleStartOverride])

  // Stable dependency for cycleStart changes
  const cycleStartMs = cycleStart?.getTime() ?? null

  // Fetch Agile rates for the cycle window (once per cycleStart change)
  useEffect(() => {
    if (!cycleStartMs) {
      setRates([])
      return
    }

    let cancelled = false
    setLoading(true)

    async function fetchRates() {
      const { data, error } = await supabase
        .from('octopus_agile_unit_rates')
        .select('valid_from, value_inc_vat_pence')
        .eq('region_code', AGILE_REGION)
        .gte('valid_from', new Date(cycleStartMs).toISOString())
        .lte('valid_from', new Date().toISOString())
        .order('valid_from', { ascending: true })

      if (!cancelled) {
        if (error) {
          console.error('[useBarrelEnergy] Failed to fetch rates:', error)
          setRates([])
        } else {
          setRates(data || [])
        }
        setLoading(false)
      }
    }

    fetchRates()
    return () => { cancelled = true }
  }, [cycleStartMs])

  // Calculate energy and cost
  const { energyKwh, costPounds, avgRatePenceKwh } = useMemo(() => {
    if (!cycleStart || !outputHistory || outputHistory.length === 0) {
      return { energyKwh: 0, costPounds: 0, avgRatePenceKwh: 0 }
    }

    const startMs = cycleStart.getTime()
    const nowMs = Date.now()

    // Build rate lookup: slot start (floored to 30 min) → pence/kWh
    const rateLookup = new Map()
    for (const r of rates) {
      const slotMs = new Date(r.valid_from).getTime()
      rateLookup.set(slotMs, r.value_inc_vat_pence / 1.20) // ex-VAT (VAT is reclaimed)
    }

    // Floor a timestamp to the start of its 30-min slot
    const floorToSlot = (ms) => ms - (ms % SLOT_DURATION_MS)

    // Group output_pct readings by 30-min slot
    const slotReadings = new Map()
    for (const reading of outputHistory) {
      const readingMs = new Date(reading.recorded_at).getTime()
      if (readingMs < startMs || readingMs > nowMs) continue
      const slotKey = floorToSlot(readingMs)
      if (!slotReadings.has(slotKey)) slotReadings.set(slotKey, [])
      slotReadings.get(slotKey).push(reading.value)
    }

    let totalEnergy = 0
    let totalCost = 0
    let totalRateWeighted = 0
    let slotsWithRate = 0

    for (const [slotMs, readings] of slotReadings) {
      const avgDuty = readings.reduce((sum, v) => sum + v, 0) / readings.length

      const slotEnd = slotMs + SLOT_DURATION_MS
      const effectiveStart = Math.max(slotMs, startMs)
      const effectiveEnd = Math.min(slotEnd, nowMs)
      const fractionHours = (effectiveEnd - effectiveStart) / (1000 * 60 * 60)

      const slotEnergy = (avgDuty / 100) * BARREL_ELEMENT_KW * fractionHours
      totalEnergy += slotEnergy

      const ratePence = rateLookup.get(floorToSlot(slotMs))
      if (ratePence != null) {
        totalCost += slotEnergy * ratePence / 100
        totalRateWeighted += ratePence * slotEnergy
        slotsWithRate += slotEnergy
      }
    }

    const avgRate = slotsWithRate > 0 ? totalRateWeighted / slotsWithRate : 0

    return {
      energyKwh: totalEnergy,
      costPounds: totalCost,
      avgRatePenceKwh: avgRate
    }
  }, [cycleStart, outputHistory, rates])

  return { cycleStart, cycleEnd, energyKwh, costPounds, avgRatePenceKwh, loading }
}

export default useBarrelEnergy
